import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { 
  ArrowRight, 
  BookOpen, 
  CheckCircle2, 
  Sparkles, 
  Shield, 
  ChevronRight,
  ExternalLink,
  Quote,
  Star,
  Globe2,
  Languages,
  Building2,
  Library,
  BookMarked
} from 'lucide-react';

export const HomePage: React.FC = () => {
  const { data: publicationsRes } = useQuery({
    queryKey: ['public-publications', { featured: 'true' }],
    queryFn: () => api.get('/public/publications?featured=true').then((res) => res.data.data),
  });

  const { data: testimonialsRes } = useQuery({
    queryKey: ['public-testimonials'],
    queryFn: () => api.get('/public/testimonials').then((res) => res.data.data),
  });

  const publications = publicationsRes || [];
  const testimonials = testimonialsRes || [];

  // International Imprint Partners
  const imprintPartners = [
    { name: 'Crossway', location: 'Wheaton, IL' },
    { name: 'The Banner of Truth Trust', location: 'Edinburgh & Carlisle' },
    { name: 'Reformation Heritage Books', location: 'Grand Rapids, MI' },
    { name: 'Christian Focus Publications', location: 'Fearn, Scotland' },
    { name: 'P&R Publishing', location: 'Phillipsburg, NJ' },
    { name: 'Lexham Press', location: 'Bellingham, WA' },
  ];

  return (
    <div className="space-y-16 pb-20">
      
      {/* 1. CORPORATE HERO SECTION - STREAMLINED & AUTHORITATIVE */}
      <section className="relative overflow-hidden bg-slate-950 text-white pt-10 sm:pt-16 pb-14 sm:pb-18 border-b border-slate-800 font-sans">
        {/* Subtle background texture */}
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#C29F78_1px,transparent_1px)] [background-size:24px_24px]"></div>
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-brand-500/10 rounded-full blur-3xl pointer-events-none"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            
            {/* Left Column: Corporate Value Proposition */}
            <div className="lg:col-span-7 space-y-5 text-center lg:text-left">
              
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-brand-950/80 border border-brand-500/30 text-brand-300 text-[11px] font-mono tracking-wider uppercase font-semibold">
                <Building2 className="w-3.5 h-3.5 text-brand-400" />
                <span>FOR THE TRUTH PRESS PRIVATE LIMITED</span>
              </div>

              <h1 className="text-3xl sm:text-5xl lg:text-5xl font-serif font-black tracking-tight leading-[1.15] text-white">
                Literature &amp; Translations of <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-300 via-brand-400 to-amber-200">Enduring Value.</span>
              </h1>

              <p className="text-sm sm:text-base text-slate-300 max-w-xl mx-auto lg:mx-0 leading-relaxed font-normal">
                India’s premier theological press and rights distributor, cultivating classical Christian scholarship, vernacular translations, and international imprint distribution across the subcontinent.
              </p>

              {/* Clean Action Buttons */}
              <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3 pt-2">
                <Link
                  to="/publications"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-brand-500 hover:bg-brand-600 text-slate-950 font-bold text-xs uppercase tracking-wider shadow-lg shadow-brand-500/20 transition-all hover:scale-[1.01]"
                >
                  <BookOpen className="w-4 h-4" />
                  <span>Explore Catalog (430+ Titles)</span>
                </Link>
                
                <Link
                  to="/contact"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-slate-200 hover:text-white font-semibold text-xs border border-slate-700 transition-all"
                >
                  <Building2 className="w-4 h-4 text-brand-400" />
                  <span>Rights &amp; Institutional Enquiries</span>
                </Link>
              </div>

              {/* Quick Trust Anchor */}
              <div className="pt-2 flex items-center justify-center lg:justify-start gap-4 text-xs text-slate-400">
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5 text-brand-400" />
                  Official Imprint: For The Truth Publishing
                </span>
                <span className="text-slate-700">&bull;</span>
                <a 
                  href="https://forthetruth.in/books" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="hover:text-brand-300 transition-colors inline-flex items-center gap-1 font-medium"
                >
                  <span>Bookstore Portal</span>
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>

            </div>

            {/* Right Column: Featured Volume Showcase Card */}
            <div className="lg:col-span-5 relative">
              <div className="relative mx-auto max-w-sm sm:max-w-md lg:max-w-none">
                
                <div className="p-5 sm:p-6 rounded-2xl bg-gradient-to-br from-slate-900 to-slate-950 border border-slate-800 shadow-2xl space-y-4 backdrop-blur-md">
                  
                  {/* Status Bar */}
                  <div className="flex items-center justify-between border-b border-slate-800 pb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                      <span className="text-[10px] font-mono uppercase tracking-wider text-slate-300 font-bold">
                        Featured Publication
                      </span>
                    </div>
                    <span className="text-[10px] font-mono text-brand-400 font-bold bg-brand-950 px-2 py-0.5 rounded border border-brand-800/40">
                      In Distribution
                    </span>
                  </div>

                  {/* Book Preview Mockup */}
                  <div className="flex gap-4 items-center">
                    <div className="w-24 sm:w-28 aspect-[3/4] rounded-lg overflow-hidden shadow-book border border-slate-700 bg-slate-900 shrink-0 flex items-center justify-center p-1.5">
                      <img
                        src="https://forthetruth.in/wp-content/uploads/2022/10/truth-for-life-front-1.jpg"
                        alt="Truth for Life: Volume 2 by Alistair Begg"
                        className="max-h-full max-w-full object-contain drop-shadow-md"
                      />
                    </div>
                    <div className="space-y-1 overflow-hidden">
                      <span className="text-[9px] font-mono uppercase font-bold text-brand-400 block tracking-wider">
                        Devotional &bull; Hardcover / PB
                      </span>
                      <h3 className="font-serif font-bold text-base text-white tracking-tight leading-snug">
                        Truth for Life: Volume 2
                      </h3>
                      <p className="text-xs text-slate-300">
                        By Alistair Begg
                      </p>
                      <p className="text-[11px] text-slate-400 line-clamp-2 pt-0.5">
                        365 daily gospel-centered reflections anchored in God&apos;s Word.
                      </p>
                      <div className="pt-1 flex items-center gap-1.5 text-[10px] text-emerald-400 font-mono font-medium">
                        <CheckCircle2 className="w-3 h-3 shrink-0" />
                        <span>Available in Bookstore</span>
                      </div>
                    </div>
                  </div>

                  {/* Direct Link Action */}
                  <div className="pt-1 flex items-center justify-between gap-3 text-xs">
                    <Link
                      to="/publications/truth-for-life-volume-2"
                      className="text-slate-300 hover:text-white font-medium inline-flex items-center gap-1"
                    >
                      <span>Catalog Details</span>
                      <ChevronRight className="w-3.5 h-3.5 text-brand-400" />
                    </Link>
                    <a
                      href="https://forthetruth.in/product/truth-for-life-volume-2/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3.5 py-1.5 rounded-lg bg-brand-500/20 hover:bg-brand-500 text-brand-300 hover:text-slate-950 font-bold border border-brand-500/30 transition-all inline-flex items-center gap-1.5"
                    >
                      <span>Store Page</span>
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>

                </div>

              </div>
            </div>

          </div>
        </div>

        {/* 4-Metric Corporate Bar */}
        <div className="mt-12 pt-7 border-t border-slate-800/80 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div className="space-y-0.5">
              <div className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">432+</div>
              <div className="text-xs text-slate-400 font-medium">Published &amp; Stocked Titles</div>
            </div>
            <div className="space-y-0.5">
              <div className="text-2xl sm:text-3xl font-extrabold text-brand-400 tracking-tight">290+</div>
              <div className="text-xs text-slate-400 font-medium">Authors Cataloged</div>
            </div>
            <div className="space-y-0.5">
              <div className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">11</div>
              <div className="text-xs text-slate-400 font-medium">Global Publishing Imprints</div>
            </div>
            <div className="space-y-0.5">
              <div className="text-2xl sm:text-3xl font-extrabold text-brand-400 tracking-tight">10+</div>
              <div className="text-xs text-slate-400 font-medium">Vernacular Languages</div>
            </div>
          </div>
        </div>

      </section>

      {/* 2. GLOBAL PUBLISHING IMPRINT PARTNERS STRIP */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="p-6 rounded-2xl bg-white border border-slate-200/90 shadow-2xs space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-100 pb-3">
            <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500 font-bold">
              Strategic Publishing &amp; Distribution Partners
            </span>
            <span className="text-xs text-slate-500">
              Direct licensing, rights, and co-publishing partnerships
            </span>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
            {imprintPartners.map((imprint, idx) => (
              <div 
                key={idx} 
                className="p-3 rounded-xl bg-slate-50 border border-slate-200/70 text-center hover:border-brand-300 hover:bg-brand-50/30 transition-all flex flex-col justify-center items-center"
              >
                <span className="font-serif font-bold text-xs sm:text-sm text-slate-900 block leading-tight">
                  {imprint.name}
                </span>
                <span className="text-[10px] text-slate-500 font-mono mt-0.5">
                  {imprint.location}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3. SPOTLIGHT ENDORSEMENT: TIM CHALLIES & CRITICAL ACCLAIM */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900 text-white border border-slate-800 shadow-xl p-8 sm:p-12">
          
          <div className="absolute top-0 right-0 -mr-16 -mt-16 w-80 h-80 bg-brand-500/10 rounded-full blur-3xl pointer-events-none"></div>

          <div className="relative space-y-6">
            
            <div className="flex items-center gap-2">
              <span className="px-3 py-1 rounded-full bg-brand-950 border border-brand-500/40 text-brand-300 text-[10px] font-mono uppercase tracking-wider font-bold">
                Critical Acclaim &amp; Endorsements
              </span>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              
              {/* Left: The Quote */}
              <div className="lg:col-span-8 space-y-5">
                <Quote className="w-10 h-10 text-brand-400/40" />

                <blockquote className="text-lg sm:text-2xl font-serif italic text-slate-100 leading-relaxed">
                  &ldquo;If you are looking for wise and perceptive guidance to help you see and experience the deep impact of God’s words in Scripture, you will love this devotional. Each day’s reflection is a gem, focusing the light of the chosen Scripture text onto our thinking, our affections and our behaviour. I cannot imagine it being read without benefit!&rdquo;
                </blockquote>

                <div className="pt-2 flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-t border-slate-800/80">
                  <div>
                    <h4 className="font-extrabold text-base text-white tracking-tight">
                      Tim Challies
                    </h4>
                    <p className="text-xs text-brand-300 font-medium">
                      Blogger; author, <em>Seasons of Sorrow</em>
                    </p>
                  </div>
                  
                  <div className="text-xs text-slate-400 font-mono">
                    Endorsement for: <strong className="text-slate-200">Truth for Life: Volume 2</strong> (Alistair Begg)
                  </div>
                </div>
              </div>

              {/* Right: Accompanying Book Thumbnail Card */}
              <div className="lg:col-span-4">
                <div className="p-4 rounded-2xl bg-slate-950/80 border border-slate-800 shadow-md flex items-center gap-4">
                  <div className="w-20 aspect-[3/4] rounded-lg overflow-hidden bg-slate-900 border border-slate-700 shrink-0 p-1 flex items-center justify-center">
                    <img
                      src="https://forthetruth.in/wp-content/uploads/2022/10/truth-for-life-front-1.jpg"
                      alt="Truth for Life Volume 2"
                      className="max-h-full max-w-full object-contain"
                    />
                  </div>
                  <div className="space-y-1 overflow-hidden">
                    <span className="text-[10px] font-mono text-brand-400 uppercase font-semibold block">
                      Featured Devotional
                    </span>
                    <h5 className="font-serif font-bold text-sm text-white truncate">
                      Truth for Life: Vol 2
                    </h5>
                    <p className="text-xs text-slate-400">
                      By Alistair Begg
                    </p>
                    <a
                      href="https://forthetruth.in/product/truth-for-life-volume-2/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-[11px] text-brand-400 hover:text-brand-300 font-bold pt-1"
                    >
                      <span>View in Bookstore</span>
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>
              </div>

            </div>

          </div>

        </div>
      </section>

      {/* 4. CORPORATE PUBLISHING OPERATIONS (REPLACES DENSE TEXT WALL) */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
        <div className="text-center space-y-2 max-w-2xl mx-auto">
          <span className="text-xs font-mono uppercase tracking-widest text-brand-600 font-bold">
            Corporate Architecture
          </span>
          <h2 className="text-2xl sm:text-3xl font-serif font-bold text-slate-900">
            Publishing Infrastructure &amp; Capabilities
          </h2>
          <p className="text-slate-600 text-xs sm:text-sm">
            Four specialized divisions powering distribution, translation rights, and academic publishing across India.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          
          {/* Pillar 1 */}
          <div className="p-6 rounded-2xl bg-white border border-slate-200/90 shadow-2xs hover:border-brand-300 hover:shadow-xs transition-all space-y-3">
            <div className="w-10 h-10 rounded-xl bg-brand-50 border border-brand-200 flex items-center justify-center text-brand-700">
              <BookMarked className="w-5 h-5" />
            </div>
            <h3 className="font-serif font-bold text-base text-slate-900">
              Pan-India Distribution
            </h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              Nationwide retail and wholesale logistics supplying bookstores, churches, academic seminaries, and readers across India with over 430 titles.
            </p>
            <div className="pt-2">
              <Link to="/publications" className="text-xs font-bold text-brand-700 hover:text-brand-800 inline-flex items-center gap-1">
                <span>Browse Catalog</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>

          {/* Pillar 2 */}
          <div className="p-6 rounded-2xl bg-white border border-slate-200/90 shadow-2xs hover:border-brand-300 hover:shadow-xs transition-all space-y-3">
            <div className="w-10 h-10 rounded-xl bg-brand-50 border border-brand-200 flex items-center justify-center text-brand-700">
              <Languages className="w-5 h-5" />
            </div>
            <h3 className="font-serif font-bold text-base text-slate-900">
              Vernacular Translations
            </h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              Licensed translation and publication of foundational Christian works into Hindi, Tamil, Telugu, Malayalam, Bengali, and other major Indian languages.
            </p>
            <div className="pt-2">
              <Link to="/services" className="text-xs font-bold text-brand-700 hover:text-brand-800 inline-flex items-center gap-1">
                <span>Translation Pipeline</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>

          {/* Pillar 3 */}
          <div className="p-6 rounded-2xl bg-white border border-slate-200/90 shadow-2xs hover:border-brand-300 hover:shadow-xs transition-all space-y-3">
            <div className="w-10 h-10 rounded-xl bg-brand-50 border border-brand-200 flex items-center justify-center text-brand-700">
              <Globe2 className="w-5 h-5" />
            </div>
            <h3 className="font-serif font-bold text-base text-slate-900">
              Global Imprint Rights
            </h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              Strategic rights licensing agreements with leading international reformational publishers to produce accessible, high-quality Indian editions.
            </p>
            <div className="pt-2">
              <Link to="/contact" className="text-xs font-bold text-brand-700 hover:text-brand-800 inline-flex items-center gap-1">
                <span>Rights Enquiries</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>

          {/* Pillar 4 */}
          <div className="p-6 rounded-2xl bg-white border border-slate-200/90 shadow-2xs hover:border-brand-300 hover:shadow-xs transition-all space-y-3">
            <div className="w-10 h-10 rounded-xl bg-brand-50 border border-brand-200 flex items-center justify-center text-brand-700">
              <Shield className="w-5 h-5" />
            </div>
            <h3 className="font-serif font-bold text-base text-slate-900">
              Academic &amp; Editorial Press
            </h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              Chicago Manual of Style editorial standards, doctrinal fidelity, classical interior typography, and durable archival-quality book production.
            </p>
            <div className="pt-2">
              <Link to="/our-process" className="text-xs font-bold text-brand-700 hover:text-brand-800 inline-flex items-center gap-1">
                <span>Publishing Process</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>

        </div>
      </section>

      {/* 5. CURATED PUBLICATIONS FROM STORE CATALOG */}
      <section className="bg-slate-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
          
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
            <div className="space-y-1 max-w-xl">
              <span className="text-xs font-mono uppercase tracking-widest text-brand-400 font-bold">
                Selected Published Works
              </span>
              <h2 className="text-2xl sm:text-3xl font-serif font-bold text-slate-100">
                Titles of Theological &amp; Pastoral Depth
              </h2>
            </div>

            <Link
              to="/publications"
              className="inline-flex items-center gap-1.5 text-xs font-bold text-brand-400 hover:text-brand-300 shrink-0 uppercase tracking-wider"
            >
              <span>View All 432 Publications</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {publications.slice(0, 4).map((book: any) => (
              <div
                key={book.id}
                className="group flex flex-col justify-between bg-slate-950 rounded-2xl overflow-hidden border border-slate-800 hover:border-brand-500/50 shadow-lg transition-all"
              >
                <div className="aspect-[3/4] bg-slate-950 overflow-hidden relative flex items-center justify-center p-4 border-b border-slate-800/80">
                  <img
                    src={book.coverImage}
                    alt={book.title}
                    className="max-h-full max-w-full object-contain drop-shadow-md group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute top-2.5 left-2.5 bg-slate-900/90 text-brand-300 text-[9px] font-mono px-2 py-0.5 rounded uppercase font-bold border border-brand-500/30">
                    {book.format}
                  </div>
                </div>

                <div className="p-4 space-y-3 flex-1 flex flex-col justify-between">
                  <div className="space-y-1">
                    <span className="text-[10px] text-brand-400 font-mono font-medium block truncate">
                      {book.category}
                    </span>
                    <h3 className="font-serif text-sm font-bold text-white group-hover:text-brand-300 transition-colors line-clamp-1">
                      {book.title}
                    </h3>
                    <p className="text-xs text-slate-400 truncate">
                      By {book.author}
                    </p>
                  </div>

                  <div className="pt-3 border-t border-slate-800/80 flex items-center justify-between text-xs">
                    <Link
                      to={`/publications/${book.slug}`}
                      className="text-white hover:text-brand-400 font-medium text-xs"
                    >
                      Details &rarr;
                    </Link>
                    {book.bookstoreUrl && (
                      <a
                        href={book.bookstoreUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-brand-400 hover:text-brand-300 font-semibold text-xs"
                      >
                        <span>Bookstore</span>
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* 6. SUPPORTING CRITICAL ACCLAIM & ACADEMIC VOICES */}
      {testimonials.length > 1 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
          <div className="text-center space-y-1 max-w-xl mx-auto">
            <span className="text-xs font-mono uppercase tracking-widest text-brand-600 font-bold">
              Scholarly Voices
            </span>
            <h2 className="text-xl sm:text-2xl font-serif font-bold text-slate-900">
              Institutional Commendation
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {testimonials.slice(1, 3).map((t: any) => (
              <div
                key={t.id}
                className="p-6 rounded-2xl bg-white border border-slate-200/90 shadow-2xs space-y-4 flex flex-col justify-between"
              >
                <p className="font-serif italic text-slate-700 text-sm sm:text-base leading-relaxed">
                  &ldquo;{t.quote}&rdquo;
                </p>
                <div className="flex items-center gap-3 pt-3 border-t border-slate-100">
                  <div className="w-10 h-10 rounded-full overflow-hidden bg-slate-200 border border-slate-200 shrink-0">
                    <img src={t.avatarUrl} alt={t.clientName} className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <h4 className="font-bold text-xs text-slate-900">{t.clientName}</h4>
                    <p className="text-[11px] text-slate-500">{t.authorTitle} &bull; <em>{t.bookTitle}</em></p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* 7. HIGH IMPACT CORPORATE & RIGHTS CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative overflow-hidden rounded-3xl bg-slate-950 text-white p-8 sm:p-12 border border-slate-800 text-center space-y-6">
          <div className="max-w-2xl mx-auto space-y-3">
            <span className="text-[11px] font-mono uppercase tracking-widest text-brand-400 font-bold">
              Institutional &amp; Publishing Inquiries
            </span>
            <h2 className="text-2xl sm:text-4xl font-serif font-bold text-slate-100">
              Partner With FOR THE TRUTH PRESS PRIVATE LIMITED
            </h2>
            <p className="text-slate-300 text-xs sm:text-sm font-normal max-w-lg mx-auto leading-relaxed">
              Whether you are an international rights holder seeking Indian distribution, an academic institution ordering in bulk, or an author submitting a scholarly manuscript.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-1">
            <Link
              to="/submit-enquiry"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-brand-500 hover:bg-brand-600 text-slate-950 font-bold text-xs uppercase tracking-wider shadow-lg shadow-brand-500/20 transition-all hover:scale-[1.01]"
            >
              <span>Submit Publishing Proposal (RFQ)</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/contact"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-slate-900 hover:bg-slate-850 text-white font-semibold text-xs border border-slate-700 transition-all"
            >
              <span>Corporate &amp; Rights Office</span>
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
};
